import type {
	AccessoInitializeEventPayload,
	AccessoPayEventPayload,
	AddedProductsToCartEventPayload,
	CheckoutUpdatePaymentEventPayload,
	ErrorEventPayload,
	GTMItem,
	GTMPaymentPlan,
	PageViewEventPayload,
	ProductImpressionEventPayload,
	ProductListViewedEventPayload,
	ProductSelectedEventPayload,
	ProductViewedEventPayload,
	PurchaseCompleteEventPayload,
	RemovedProductsFromCartEventPayload,
	SelectContentEventPayload,
	SelectContentPayload,
	SelectNavigationEventPayload,
	SequenceBeginEventPayload,
	SequenceCompletedEventPayload,
	SequenceStepSkippedEventPayload,
	SequenceStepViewedEventPayload,
	ValuesOf,
} from '@accesso/cc-std-lib';
import type { Provider } from '@angular/core';
import { DEFAULT_CURRENCY_CODE, inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';

import { EventName, EventService, getProperty, GtmEventName, GtmEventService, isNonEmptyString, LoggerService } from '@accesso/cc-std-lib';
import { ProductModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';
import { BreadcrumbSourceEvent } from '@accesso/ng-uds/breadcrumbs';
import { MegaMenuEventSource } from '@accesso/ng-uds/mega-menu';
import { VerticalNavigationEventSource } from '@accesso/ng-uds/vertical-navigation';

import type { IEventMapperService } from './event-mapper.models';
import type { ProductImpression, ProductModelToGtmProductOptions } from './event-mapper.utils';
import { CartStore } from '../../feature/cart/cart.store';
import { MerchantStore } from '../../feature/merchant/merchant.store';
import { EnvironmentService } from '../environment/environment.service';
import { EventMapperService } from './event-mapper.service';
import {
	basketItemToGtmItem,
	getBasketEventDetails,
	productImpressionsToGtmProductList,
	productImpressionToGtmProduct,
	productModelToGtmModel,
} from './event-mapper.utils';

const SequencePostion = <const>{
	BEGIN: 'begin',
	END: 'end',
};

@Injectable({
	providedIn: 'root',
})
export class EventMapperBrowserService implements IEventMapperService {
	private readonly logger = inject(LoggerService);
	private readonly jsonApi = inject(JsonApiService);
	private readonly currency = inject(DEFAULT_CURRENCY_CODE);
	private readonly gtm = inject(GtmEventService);
	private readonly basket = inject(CartStore).basket;

	/**
	 * Our constructor. Subscribes to all the events.
	 * @constructor
	 */
	constructor() {
		inject(EventService).events$.subscribe((event) => {
			switch (event.event) {
				case EventName.PAGE_VIEW:
					return this.handlePageView(event);
				case EventName.PRODUCT_VIEWED:
					return this.handleProductViewed(event);
				case EventName.PRODUCT_IMPRESSION:
					return this.handleProductImpression(event);
				case EventName.PRODUCT_SELECTED:
					return this.handleProductSelected(event);
				case EventName.PRODUCT_LIST_VIEWED:
					return this.handleProductListViewed(event);
				case EventName.PRODUCT_ADD_TO_CART:
					return this.handleProductAddToCart(event);
				case EventName.PRODUCT_REMOVE_FROM_CART:
					return this.handleProductRemoveFromCart(event);
				case EventName.SELECT_CONTENT:
					return this.handleSelectContent(event);
				case EventName.SELECT_NAVIGATION:
					return this.handleSelectNavigation(event);
				case EventName.SEQUENCE_BEGIN:
					return this.handleSequenceBeginOrCompleted(SequencePostion.BEGIN, event);
				case EventName.SEQUENCE_STEP_VIEWED:
					return this.handleSequenceStepViewed(event);
				case EventName.SEQUENCE_STEP_SKIPPED:
					return this.handleSequenceStepSkipped(event);
				case EventName.SEQUENCE_COMPLETED:
					return this.handleSequenceBeginOrCompleted(SequencePostion.END, event);
				case EventName.VIEW_CART:
					return this.handleViewCart();
				case EventName.VIEW_MINI_CART:
					return this.handleViewMiniCart();
				case EventName.ERROR:
					return this.handleError(event);
				case EventName.CHECKOUT_BEGIN:
					return this.handleCheckoutBegin();
				case EventName.CHECKOUT_UPDATE_PAYMENT:
					return this.handlePaymentSelected(event);
				case EventName.PURCHASE_COMPLETE:
					return this.handlePurchaseComplete(event);
				case EventName.ACCESSO_PAY:
					return this.handleAccessoPay(event);
				case EventName.INITIALIZE:
					return this.handleInitialize(event);
				default:
					this.logger.warn(`[EventMapperBrowserService] case not handled for event "${event.event}"`);
			}
		});
	}

	/**
	 * Fires the sequence begin or completed GTM event
	 * @param position The position
	 * @param event The sequence event
	 */
	handleSequenceBeginOrCompleted(
		position: ValuesOf<typeof SequencePostion>,
		{ details: { sequenceId, type, totalSteps, name, data } }: SequenceBeginEventPayload | SequenceCompletedEventPayload
	): void {
		this.gtm.fire({
			event: position === SequencePostion.BEGIN ? GtmEventName.SEQUENCE_BEGIN : GtmEventName.SEQUENCE_COMPLETED,
			details: { sequenceId, type, totalSteps, name, data },
		});
	}

	/**
	 * Handles when a sequence is viewed
	 * @param event The SequenceStepViewedEventPayload
	 */
	handleSequenceStepViewed({ details: { sequenceId, type, component, step, totalSteps, name, data } }: SequenceStepViewedEventPayload): void {
		this.gtm.fire({
			event: GtmEventName.SEQUENCE_STEP_VIEWED,
			details: { sequenceId, type, component, step, totalSteps, name, data },
		});
	}

	/**
	 * Handles when a sequence is skipped
	 * @param event The SequenceStepSkippedEventPayload
	 */
	handleSequenceStepSkipped({ details: { sequenceId, type, component, step, totalSteps, reason, name, data } }: SequenceStepSkippedEventPayload): void {
		this.gtm.fire({
			event: GtmEventName.SEQUENCE_STEP_SKIPPED,
			details: { sequenceId, type, component, step, totalSteps, reason, name, data },
		});
	}

	/**
	 * Called when the user is shown an error
	 */
	handleError(event: ErrorEventPayload): void {
		this.gtm.fire({
			event: GtmEventName.ERROR,
			details: {
				errorType: event.details.error_type,
				errorCode: (event.details.code as unknown as number) ?? -1,
				errorLabel: event.details.message ?? '',
				data: event.details.data,
			},
		});
	}

	/**
	 * Called whenever a user chooses a payment method
	 */
	handlePaymentSelected({ details: { type, data } }: CheckoutUpdatePaymentEventPayload): void {
		const basket = this.basket();
		if (!basket) return;

		this.gtm.fire({
			event: GtmEventName.ADD_PAYMENT_INFO,
			details: {
				cartId: basket.id,
				paymentTypeName: type,
				data,
			},
		});
	}

	/**
	 * Called when the user starts the checkout process
	 */
	handlePurchaseComplete(event: PurchaseCompleteEventPayload): void {
		const basket = this.basket();
		if (!basket) return;

		const { transactionId, currency, total, subtotal, tax, fees, discount, data } = event.details;

		if (!transactionId) return;

		this.gtm.fire({
			event: GtmEventName.PURCHASE,
			details: {
				cartId: basket.id,
				transactionId,
				currency,
				retail: subtotal,
				tax,
				fees,
				discount,
				total,
				items: basket.items().map((item, index) => basketItemToGtmItem(item, index) as GTMItem & { payment_plan: GTMPaymentPlan }),
				data,
			},
		});
	}

	/**
	 * Called when the user starts the checkout process
	 */
	handleCheckoutBegin(): void {
		const basket = this.basket();
		if (!basket) return;

		const { retail, tax, fees, due } = basket.summary();

		this.gtm.fire({
			event: GtmEventName.BEGIN_CHECKOUT,
			details: {
				cartId: basket.id,
				currency: this.currency,
				retail,
				tax,
				fees,
				discount: 0,
				total: parseFloat((retail + tax + fees).toFixed(2)),
				adjustments: {
					cartFee: 0,
					cartDiscount: 0,
				},
				items: basket.items().map((item, index) => basketItemToGtmItem(item, index)),
				data: {
					due,
				},
			},
		});
	}

	/**
	 * Handles the product view event
	 * @param event The product viewed event
	 */
	handleProductViewed({ details: { id, data } }: ProductViewedEventPayload): void {
		const product = this.jsonApi.findOne(ProductModel, <string>id);
		this.gtm.fire({
			event: GtmEventName.VIEW_PRODUCT,
			details: {
				currency: this.currency,
				product: productModelToGtmModel(product),
				data,
			},
		});
	}

	/**
	 * Handles the impression  event
	 * @param event The impression content event
	 */
	handleProductImpression(event: ProductImpressionEventPayload): void {
		const { value, source, data } = event.details;

		const products: ProductImpression[] = value.map(({ index, id }) => ({
			index,
			product: this.jsonApi.findOne(ProductModel, <string>id),
		}));

		const categoryId = data?.['categoryId'] as string;
		const options: ProductModelToGtmProductOptions = {
			view: source,
			categoryId,
		};

		this.gtm.fire({
			event: GtmEventName.IMPRESSION,
			details: {
				currency: this.currency,
				products: productImpressionsToGtmProductList(products, options),
			},
		});
	}

	/**
	 * Handles the selected item event
	 * @param event The select item event
	 */
	handleProductSelected(event: ProductSelectedEventPayload): void {
		const {
			value: { index, id },
			source,
			listId,
			type,
			data: eventData,
		} = event.details;
		const product = this.jsonApi.findOne(ProductModel, <string>id);

		const data: Record<string, unknown> = {
			listId,
			type,
			...eventData,
		};

		this.gtm.fire({
			event: GtmEventName.SELECT_PRODUCT,
			details: {
				currency: this.currency,
				product: productImpressionToGtmProduct(
					{
						index,
						product,
					},
					{
						view: source,
						categoryId: data['categoryId'] as string,
					}
				),
				data,
			},
		});
	}

	/**
	 * Handles the view list event
	 * @param event The viewlist event
	 */
	handleProductListViewed(event: ProductListViewedEventPayload): void {
		const { listId: identifier, type, source, value, data } = event.details;
		const categoryId = (data ?? {})['categoryId'] as string;

		const products = value.map(
			({ index, id }): ProductImpression => ({
				index,
				product: this.jsonApi.findOne(ProductModel, <string>id),
			})
		);

		this.gtm.fire({
			event: GtmEventName.VIEW_LIST,
			details: {
				categoryId,
				identifier,
				type,
				source,
				products: productImpressionsToGtmProductList(products, {
					view: source,
					categoryId,
				}),
			},
		});
	}

	/**
	 * Called when a product is added to the cart
	 * @param event The event
	 */
	handleProductAddToCart(event: AddedProductsToCartEventPayload): void {
		const { items: eventItems, data } = event.details;
		const basket = this.basket();
		if (!basket) return;

		const basketItems = basket.items();

		const items = eventItems.reduce((acc: GTMItem[], { itemId }) => {
			const foundItem = basketItems.find((item) => item.id === itemId);
			if (!foundItem) return acc;

			acc.push(basketItemToGtmItem(foundItem, basketItems.indexOf(foundItem)));

			return acc;
		}, []);

		if (items.length) {
			this.gtm.fire({
				event: GtmEventName.ADD_TO_CART,
				details: {
					cartId: basket.id,
					currency: this.currency,
					items,
					data,
				},
			});
		}
	}

	/**
	 * Called when a product is removed from the cart
	 * @param event The event
	 */
	handleProductRemoveFromCart(event: RemovedProductsFromCartEventPayload): void {
		const { items: eventItems, data } = event.details;
		const basket = this.basket();
		if (!basket) return;

		const basketItems = basket.items();

		const items = eventItems.reduce((acc: GTMItem[], { itemId }) => {
			const foundItem = basketItems.find((item) => item.id === itemId);
			if (!foundItem) return acc;

			acc.push(basketItemToGtmItem(foundItem, basketItems.indexOf(foundItem)));

			return acc;
		}, []);

		if (items.length) {
			this.gtm.fire({
				event: GtmEventName.REMOVE_FROM_CART,
				details: {
					cartId: basket.id,
					currency: this.currency,
					items,
					data,
				},
			});
		}
	}

	/**
	 * Handles the select content event
	 * @param event The event
	 */
	handleSelectContent(event: SelectContentEventPayload): void {
		const { value: label, type, source, data, external } = event.details;
		this.gtm.fire({
			event: GtmEventName.SELECT_CONTENT,
			details: {
				label,
				type: type ?? 'unknown',
				source,
				data,
				external,
			},
		});
	}

	/**
	 * Handles selecting navigation items
	 * @param event The event
	 */
	handleSelectNavigation(event: SelectNavigationEventPayload): void {
		const {
			data,
			source,
			external,
			type,
			value: { label },
		} = event.details;

		const identifier = getProperty(data as Record<string, string>, 'item.id') as string;
		const gtmEvent: SelectContentPayload = {
			event: GtmEventName.SELECT_CONTENT,
			details: {
				identifier,
				label,
				type: 'unknown',
				external,
				data: {
					type,
				},
			},
		};

		switch (source) {
			case MegaMenuEventSource:
				gtmEvent.details.type = 'desktop_navigation';
				break;
			case BreadcrumbSourceEvent:
				gtmEvent.details.type = 'breadcrumb';
				break;
			case VerticalNavigationEventSource:
				gtmEvent.details.type = 'vertical_navigation';
				break;
		}

		this.gtm.fire(gtmEvent);
	}

	/**
	 * Handles the page view event
	 * @param event The event
	 */
	handlePageView(event: PageViewEventPayload): void {
		const { url: path, normalizedUrl: route, data } = event.details;
		let query: Record<string, string> = {};

		if (data?.['query']) {
			query = data['query'] as Record<string, string>;
		}

		this.gtm.fire({
			event: GtmEventName.PAGEVIEW,
			details: {
				path,
				route,
				query,
			},
		});
	}

	/**
	 * Called when the user views the main cart page
	 */
	handleViewCart(): void {
		const basket = this.basket();
		if (!basket) return;

		this.gtm.fire({
			event: GtmEventName.VIEW_CART,
			details: {
				cartId: basket.id,
				...getBasketEventDetails(this.currency, basket),
			},
		});
	}

	/**
	 * Called when the user views the mini cart
	 */
	handleViewMiniCart(): void {
		const basket = this.basket();
		if (!basket) return;

		this.gtm.fire({
			event: GtmEventName.VIEW_MINI_CART,
			details: {
				cartId: basket.id,
				...getBasketEventDetails(this.currency, basket),
			},
		});
	}

	/**
	 * Handles sending ALL accesso pay events
	 */
	async handleAccessoPay(event: AccessoPayEventPayload): Promise<void> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const eventDetails = event.details as any;
		if (!eventDetails) return;
		const details: Record<string, unknown> = {
			key: eventDetails.type,
		};

		if (eventDetails?.type === 'BillingDemographics') {
			const payload = eventDetails.payload as Record<string, unknown>;
			const newPayload: Record<string, string> = {};

			['firstName', 'lastName', 'country', 'stateProvince'].forEach((key) => {
				if (isNonEmptyString(payload?.[key])) newPayload[key] = payload[key];
			});

			if (isNonEmptyString(payload?.['email'])) newPayload['email'] = await this.emailToHash(payload['email']);

			details['payload'] = newPayload;
		} else {
			details['payload'] = eventDetails.payload;
		}

		this.gtm.fire({
			event: GtmEventName.ACCESSO_PAY,
			details,
		});
	}

	/**
	 * Called once the application is initialized
	 */
	handleInitialize(event: AccessoInitializeEventPayload): void {
		const { language, merchantId, tenant, venue, industry, data } = event.details;

		this.gtm.fire({
			event: GtmEventName.INITIALIZE,
			details: {
				language,
				merchantId,
				tenant,
				venue,
				industry,
				data,
			},
		});
	}

	/**
	 * Converts an email to a hash
	 * @param email The email to convert
	 */
	emailToHash(email: string): Promise<string> {
		const emailAddress = new TextEncoder().encode(email.toLowerCase().trim());
		const hashEmailAddress = crypto.subtle.digest('SHA-256', emailAddress);
		return hashEmailAddress.then((arrayBuffer) => {
			const hashArray = Array.from(new Uint8Array(arrayBuffer));
			return hashArray.map((h) => h.toString(16).padStart(2, '0')).join('');
		});
	}
}

export function provideEventMapperBrowserService(): Provider {
	return {
		provide: EventMapperService,
		useClass: EventMapperBrowserService,
	};
}

export const eventMapperInitializer = () => {
	const injector = inject(Injector);
	const merchant = inject(MerchantStore);
	const environment = inject(EnvironmentService);

	if (environment.store.includes('localhost') || !environment.store) return;

	return toObservable(merchant.initialized).pipe(
		filter((initialized) => initialized),
		take(1),
		map(() => injector.get(EventMapperService))
	);
};
